import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface UserAuthEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  currentEmail: string;
}

export function UserAuthEditModal({
  open,
  onOpenChange,
  userId,
  currentEmail
}: UserAuthEditModalProps) {
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const handleUpdateEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error("Digite um email válido");
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase.functions.invoke('admin-update-user-auth', {
        body: {
          action: 'update-email',
          user_id: userId,
          new_email: newEmail
        }
      });

      if (error) throw error;

      toast.success("Email atualizado com sucesso");
      onOpenChange(false);
      setNewEmail("");
    } catch (error) {
      console.error("Erro ao atualizar email:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao atualizar email");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase.functions.invoke('admin-update-user-auth', {
        body: {
          action: 'reset-password',
          user_id: userId
        }
      });

      if (error) throw error;

      toast.success("Email de redefinição de senha enviado");
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao enviar email de reset:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao enviar email de redefinição");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Alterar Dados de Acesso</DialogTitle>
          <DialogDescription>
            Gerencie o email e senha de acesso do profissional à plataforma.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Email Atual */}
          <div className="space-y-2">
            <Label>Email Atual</Label>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{currentEmail}</span>
            </div>
          </div>

          {/* Novo Email */}
          <div className="space-y-2">
            <Label htmlFor="new-email">Novo Email</Label>
            <Input
              id="new-email"
              type="email"
              placeholder="novo-email@exemplo.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={loading}
            />
            <Button
              onClick={handleUpdateEmail}
              disabled={loading || !newEmail}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Atualizando...
                </>
              ) : (
                "Atualizar Email"
              )}
            </Button>
          </div>

          {/* Redefinir Senha */}
          <div className="space-y-2 pt-4 border-t">
            <Label>Redefinir Senha</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Enviar um email com link para o profissional redefinir a senha.
            </p>
            <Button
              onClick={handleResetPassword}
              disabled={loading}
              variant="outline"
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enviando...
                </>
              ) : (
                "Enviar Email de Redefinição"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
